import { useCallback, useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AdminSidebar } from './admin-sidebar';
import { AdminHeader } from './admin-header';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useModelConfigStatus } from '@/hooks/useModelConfigStatus';
import { ModelSetupWizardModal } from '@/features/admin/components/model-setup-wizard-modal';
import { AdminTenantProvider } from '@/contexts';
import { cn } from '@/lib/utils';

export function AdminLayout() {
  // 移动端侧边栏开关状态
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  // 桌面端侧边栏收起状态
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] =
    useState(false);

  const { isAdminAuthenticated, isAdminLoading } = useAdminAuth();
  const {
    isLoading: isConfigLoading,
    isFullyConfigured,
    missingTypes,
    defaultTenantId,
    refreshStatus,
  } = useModelConfigStatus();

  const navigate = useNavigate();

  // 切换侧边栏显示状态(移动端)
  const handleToggleSidebar = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  // 关闭侧边栏(移动端)
  const handleCloseSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  // 切换侧边栏收起状态(桌面端)
  const handleToggleCollapse = useCallback(() => {
    setIsDesktopSidebarCollapsed((prev) => !prev);
  }, []);

  // 认证检查：未认证且加载完成时重定向到登录页
  useEffect(() => {
    if (!isAdminLoading && !isAdminAuthenticated) {
      console.log('🛡️ 管理员未认证，重定向到管理后台登录页');
      navigate('/admin/login', { replace: true });
    }
  }, [isAdminAuthenticated, isAdminLoading, navigate]);

  // 模型配置完成回调
  const handleSetupComplete = useCallback(async () => {
    console.log('✅ 模型配置完成');
    await refreshStatus();
    // 配置完成后导航到模型管理页面
    navigate('/admin/model-management', { replace: true });
  }, [refreshStatus, navigate]);

  // 显示加载状态
  if (isAdminLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">
            正在验证管理员登录状态...
          </p>
        </div>
      </div>
    );
  }

  // 未认证则不渲染内容（useEffect会处理重定向）
  if (!isAdminAuthenticated) {
    return null;
  }

  return (
    <>
      <div
        className="flex min-h-screen overflow-auto p-3 gap-2"
        style={{ backgroundColor: '#F0F4F8' }}
      >
        {/* 桌面端侧边栏 - 固定定位 */}
        <AdminSidebar
          className="hidden lg:flex fixed left-3 top-3 z-30 rounded-xl shadow-sm"
          style={{ height: 'calc(100vh - 24px)' }}
          isCollapsed={isDesktopSidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />

        {/* 移动端遮罩 */}
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden animate-fade-in"
            onClick={handleCloseSidebar}
            aria-hidden="true"
          />
        )}

        {/* 移动端侧边栏 - 滑入式 */}
        <AdminSidebar
          className={cn(
            'fixed left-0 top-0 z-50 h-full lg:hidden transition-transform duration-300 ease-in-out',
            isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          isCollapsed={false}
          onToggleCollapse={handleCloseSidebar}
        />

        {/* Main Content - 为侧边栏留出空间 */}
        <div
          className={cn(
            'flex flex-1 flex-col h-full transition-all duration-300 relative',
            isDesktopSidebarCollapsed ? 'lg:ml-[4.25rem]' : 'lg:ml-[15.75rem]'
          )}
        >
          {/* 固定的Header */}
          <div
            className="sticky top-0 z-20 px-4 pb-2"
            style={{ backgroundColor: '#F0F4F8' }}
          >
            <AdminHeader onToggleSidebar={handleToggleSidebar} />
          </div>

          {/* 页面内容 - 独立滚动区域 */}
          <main
            className="flex-1 overflow-y-auto px-4 pt-0"
            style={{ backgroundColor: '#F0F4F8' }}
          >
            <div className="page-enter pb-4">
              <AdminTenantProvider>
                <Outlet />
              </AdminTenantProvider>
            </div>
          </main>
        </div>
      </div>

      {/* 模型配置向导弹窗 - 未配置完成时强制显示 */}
      <ModelSetupWizardModal
        open={isAdminAuthenticated && !isConfigLoading && !isFullyConfigured}
        missingTypes={missingTypes}
        tenantId={defaultTenantId}
        onComplete={handleSetupComplete}
      />
    </>
  );
}
